import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Button, Checkbox, Group, MenuItemProps, Text, Transition, useMantineTheme } from '@mantine/core';
import { IconCircleCheckFilled, IconCircleOff, IconTrash } from '@tabler/icons-react';
import Menu from 'components/Menu';
import ModalLateCancelNoShow from 'components/ModalLateCancelNoShow';
import Table from 'components/Table';
import { useLocalization } from 'context/localization-context';
import dayjs from 'dayjs';
import useAppointment from 'hooks/appointment/useAppointment';
import useDeleteAthleteAppointment from 'hooks/athleteAppointment/useDeleteAthleteAppointment';
import useUpdateAthleteAppointment from 'hooks/athleteAppointment/useUpdateAthleteAppointment';
import useDeleteAthleteClass from 'hooks/athleteClass/useDeleteAthleteClass';
import useUpdateAthleteClass from 'hooks/athleteClass/useUpdateAthleteClass';
import useAthleteEvents from 'hooks/athleteEvent/useAthleteEvents';
import useDeleteAthleteEvents from 'hooks/athleteEvent/useDeleteAthleteEvents';
import useClass from 'hooks/class/useClass';
import DeleteAttendanceModal from 'pages/private/owner/AthleteDetail/components/Attendance/components/DeleteAttendanceModal';
import { ReservationType, WeekdayType } from 'pages/private/owner/AthleteDetail/components/Attendance/types';
import CoachCell from 'pages/private/owner/AthleteDetail/components/CoachCell';
import { CellProps, HeaderProps } from 'react-table';
import { AthleteEvent } from 'types';
import { getDayFromDate } from 'utils/event.helpers';
import notificationHandler from 'utils/notifications';

interface ItemProps extends MenuItemProps {
  title: string;
  onClick: () => void;
  color: string;
  disabled?: boolean;
}

interface IAthleteAttendanceTableContainerProps {
  searchFilters: {
    date?: [Date | null, Date | null];
    events?: string[];
    athlete?: string;
    athletePackage?: string;
    weekdays?: WeekdayType['day'][];
    isBulkAdd?: ReservationType['type'] | null;
  };
  columnsOverride?: any;
  permission?: boolean;
  timeType?: 'past' | 'upcoming';
  limit?: string;
  defaultEndDate?: string;
  setOrderingValue?: React.Dispatch<React.SetStateAction<string>>;
  margin?: number;
  bgc?: 'white' | 'gray';
  athleteName?: string;
  showProfileParityColumns?: boolean;
}

const AthleteAttendanceTableContainer = ({
  columnsOverride,
  searchFilters,
  permission = true,
  timeType,
  limit,
  defaultEndDate,
  setOrderingValue,
  margin = 12,
  bgc = 'white',
  athleteName = '',
  showProfileParityColumns = false,
}: IAthleteAttendanceTableContainerProps): JSX.Element => {
  const theme = useMantineTheme();
  const { dateFormat } = useLocalization();
  const { successNotification, errorNotification } = notificationHandler();
  const deleteAthleteClassMutation = useDeleteAthleteClass();
  const deleteAthleteAppointmentMutation = useDeleteAthleteAppointment();
  const deleteAthleteEventsMutation = useDeleteAthleteEvents();
  const updateAthleteClassMutation = useUpdateAthleteClass();
  const updateAthleteAppointmentMutation = useUpdateAthleteAppointment();

  const [tableData, setTableData] = useState<AthleteEvent[]>([]);
  const [ordering, setOrdering] = useState('startDate');
  const [ascending, setAscending] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedLcNsEvent, setSelectedLcNsEvent] = useState<AthleteEvent | null>(null);
  const [lcNsIsLateCancel, setLcNsIsLateCancel] = useState(true);

  const { data: lcNsClassData } = useClass(
    selectedLcNsEvent?.type === 'athlete_kilo_class' ? selectedLcNsEvent.eventId : undefined,
  );
  const { data: lcNsAppointmentData } = useAppointment(
    selectedLcNsEvent?.type === 'athlete_appointment' ? selectedLcNsEvent.eventId : undefined,
  );

  const lcNsCancellationSettings =
    selectedLcNsEvent?.type === 'athlete_kilo_class'
      ? lcNsClassData?.data.reservationAndCancellationSettings
      : lcNsAppointmentData?.data.cancellationSettings;

  const lcNsEventName =
    selectedLcNsEvent?.type === 'athlete_kilo_class'
      ? lcNsClassData?.data.kiloClassType.name ?? ''
      : lcNsAppointmentData?.data.appointmentType?.name ?? '';

  const { data, fetchNextPage, hasNextPage, isSuccess, isError, isLoading, isFetchingNextPage } =
    useAthleteEvents({
      ...searchFilters,
      startDateRange:
        searchFilters.date && searchFilters.date[0]
          ? dayjs(searchFilters.date[0]).format('YYYY-MM-DD')
          : undefined,
      endDateRange:
        searchFilters.date && searchFilters.date[1]
          ? dayjs(searchFilters.date[1]).format('YYYY-MM-DD')
          : defaultEndDate,
      hideWaitlist: false,
      ordering: ascending ? ordering : `-${ordering}`,
      type: timeType,
      weekdays: searchFilters.weekdays,
      isBulkAdd: searchFilters.isBulkAdd ? searchFilters.isBulkAdd === 'true' : undefined,
      limit,
    });

  const handleHeaderClick = useCallback(
    (header: string): void => {
      setOrdering(header);
      setAscending(a => {
        setOrderingValue?.(!a ? header : `-${header}`);
        return !a;
      });
    },
    //eslint-disable-next-line react-hooks/exhaustive-deps
    [ascending],
  );

  const renderAttendedStatus = (row: AthleteEvent): JSX.Element => {
    if (row.isLateCancel) {
      return <Text>Late Cancel</Text>;
    } else if (row.isNoShow) {
      return <Text>No Show</Text>;
    } else if (row.isCheckedIn) {
      return <IconCircleCheckFilled style={{ color: theme.colors.kiloBlue[6] }} />;
    }
    return <IconCircleOff color='gray' />;
  };

  const renderReservedStatus = (row: AthleteEvent): JSX.Element => {
    if (row.isReserved) {
      return <IconCircleCheckFilled style={{ color: theme.colors.kiloBlue[6] }} />;
    } else if (row.isWaitlisted) {
      return <Text>Waitlist</Text>;
    }
    return <IconCircleOff color='gray' />;
  };

  const handleDeleteAthleteEvent = useCallback(
    (athleteEvent: AthleteEvent): void => {
      const { type, athleteEventId } = athleteEvent;
      if (type === 'athlete_kilo_class') {
        deleteAthleteClassMutation.mutate(athleteEventId, {
          onSuccess: () => {
            successNotification('Attendance record delete success.');
          },
          onError: error => {
            errorNotification('Attendance record delete fail.', error.response?.data.message);
          },
        });
      }
      if (type === 'athlete_appointment') {
        deleteAthleteAppointmentMutation.mutate(athleteEventId, {
          onSuccess: () => {
            successNotification('Attendance record delete success.');
          },
          onError: error => {
            errorNotification('Attendance record delete fail.', error.response?.data.message);
          },
        });
      }
      if (type === 'imported_attendance') {
        deleteAthleteEventsMutation.mutate(
          { data: { athleteEvents: [{ athleteEventId, type }] } },
          {
            onSuccess: () => successNotification('Attendance record delete success.'),
            onError: error =>
              errorNotification('Attendance record delete fail.', error.response?.data.message),
          },
        );
      }
    },
    [
      deleteAthleteAppointmentMutation,
      deleteAthleteClassMutation,
      deleteAthleteEventsMutation,
      errorNotification,
      successNotification,
    ],
  );

  const handleAthleteEventUpdate = useCallback(
    (athleteEvent: AthleteEvent): void => {
      const { athleteEventId, type, eventId } = athleteEvent;
      if (type === 'athlete_kilo_class' && searchFilters.athlete) {
        updateAthleteClassMutation.mutate(
          {
            athleteKiloClassId: athleteEventId,
            athlete: searchFilters.athlete,
            kiloClass: eventId,
            ...athleteEvent,
            isKioskSelfService: false,
          },
          {
            onSuccess: () => {
              successNotification('Attendance record update success.');
            },
            onError: error => {
              errorNotification('Attendance record update failed', error.response?.data.message ?? '');
            },
          },
        );
      }
      if (type === 'athlete_appointment' && searchFilters.athlete) {
        updateAthleteAppointmentMutation.mutate(
          {
            athleteAppointmentId: athleteEventId,
            athlete: searchFilters.athlete,
            appointment: eventId,
            ...athleteEvent,
          },
          {
            onSuccess: () => {
              successNotification('Attendance record update success.');
            },
            onError: error => {
              errorNotification('Attendance record update failed', error.response?.data.message ?? '');
            },
          },
        );
      }
    },
    [
      errorNotification,
      searchFilters.athlete,
      successNotification,
      updateAthleteAppointmentMutation,
      updateAthleteClassMutation,
    ],
  );

  const getMenuItems = (athleteEvent: AthleteEvent): ItemProps[] => {
    if (athleteEvent.isImported) {
      return [
        {
          title: 'Delete',
          onClick: () => handleDeleteAthleteEvent(athleteEvent),
          display: 'block',
          color: 'red',
        },
      ];
    }
    let items: ItemProps[] = [
      {
        title: 'Delete',
        onClick: () => handleDeleteAthleteEvent(athleteEvent),
        display: 'block',
        color: 'red',
      },
    ];
    if (!athleteEvent.isLateCancel && !athleteEvent.isNoShow) {
      items = [
        {
          title: 'Mark As Unreserved',
          onClick: () => handleAthleteEventUpdate({ ...athleteEvent, isReserved: false }),
          color: 'black',
          display: athleteEvent.isReserved ? 'block' : 'none',
          disabled: !athleteEvent.isCheckedIn,
        },
        {
          title: 'Mark As Reserved',
          onClick: () =>
            handleAthleteEventUpdate({ ...athleteEvent, isReserved: true, isWaitlisted: false }),
          color: 'black',
          display: !athleteEvent.isReserved ? 'block' : 'none',
        },
        {
          title: 'Mark As Attended',
          onClick: () =>
            handleAthleteEventUpdate({ ...athleteEvent, isCheckedIn: true, isWaitlisted: false }),
          color: 'black',
          display: !athleteEvent.isCheckedIn ? 'display' : 'none',
        },
        {
          title: 'Mark As Unattended',
          onClick: () => handleAthleteEventUpdate({ ...athleteEvent, isCheckedIn: false }),
          color: 'black',
          display: athleteEvent.isCheckedIn ? 'display' : 'none',
          disabled: !athleteEvent.isReserved,
        },
        ...items,
      ];
      if (athleteEvent.isReserved && athleteEvent.isCredited && !athleteEvent.isCheckedIn) {
        items = [
          {
            title: 'Mark Late Cancelled',
            onClick: () => {
              setSelectedLcNsEvent(athleteEvent);
              setLcNsIsLateCancel(true);
            },
            color: 'black',
          },
          {
            title: 'Mark No Show',
            onClick: () => {
              setSelectedLcNsEvent(athleteEvent);
              setLcNsIsLateCancel(false);
            },
            color: 'black',
          },
          ...items,
        ];
      }
    }
    return items;
  };

  useEffect(() => {
    if (!isLoading && data) {
      setTableData(data.pages.map(page => page.data.results).flat());
    }
  }, [isLoading, data, setTableData]);

  useEffect(() => {
    setSelectedIds(new Set());
  }, [searchFilters]);

  const selectedEvents = useMemo(
    () => tableData.filter(event => selectedIds.has(event.athleteEventId)),
    [tableData, selectedIds],
  );

  const handleSelectRow = useCallback((eventId: string): void => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(eventId)) {
        newSet.delete(eventId);
      } else {
        newSet.add(eventId);
      }
      return newSet;
    });
  }, []);

  const handleSelectAll = useCallback((): void => {
    if (selectedIds.size === tableData.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(tableData.map(e => e.athleteEventId)));
    }
  }, [tableData, selectedIds.size]);

  const handleBulkDelete = useCallback((): void => {
    setDeleteModalOpen(true);
  }, []);

  const handleDeleteModalClose = useCallback((): void => {
    setDeleteModalOpen(false);
    setSelectedIds(new Set());
  }, []);

  const columns = [
    {
      id: 'select',
      Header: ({ rows }: HeaderProps<AthleteEvent>) =>
        permission ? (
          <Checkbox
            checked={rows.length > 0 && selectedIds.size === rows.length}
            indeterminate={selectedIds.size > 0 && selectedIds.size < rows.length}
            onChange={handleSelectAll}
            size='md'
            radius='xs'
          />
        ) : null,
      Cell: ({ row }: CellProps<AthleteEvent>) =>
        permission ? (
          <Checkbox
            checked={selectedIds.has(row.original.athleteEventId)}
            onChange={() => handleSelectRow(row.original.athleteEventId)}
            size='md'
            radius='xs'
          />
        ) : null,
      width: '15px',
      minWidth: '15px',
      maxWidth: '15px',
      disableSortBy: true,
    },
    {
      Header: 'Date',
      accessor: 'startDate' as const,
      Cell: ({ value }: CellProps<AthleteEvent>) => dayjs(value).format(dateFormat),
    },
    {
      Header: 'Time',
      accessor: 'startTime' as const,
      Cell: ({ row }: CellProps<AthleteEvent>) =>
        dayjs(`${row.original.startDate} ${row.original.startTime}`).format('h:mm A'),
      disableSortBy: true,
    },
    {
      Header: 'Event',
      accessor: 'name' as const,
    },
    ...(showProfileParityColumns
      ? [
          {
            Header: 'Coach',
            accessor: 'coachName' as const,
            Cell: ({ row }: CellProps<AthleteEvent>) => <CoachCell coachName={row.original.coachName} />,
          },
        ]
      : []),
    {
      Header: 'Reserved',
      accessor: 'isReserved' as const,
      Cell: ({ row }: CellProps<AthleteEvent>) => renderReservedStatus(row.original),
    },
    {
      Header: 'Attended',
      accessor: 'isCheckedIn' as const,
      Cell: ({ row }: CellProps<AthleteEvent>) => renderAttendedStatus(row.original),
      disableSortBy: true,
    },
    ...(showProfileParityColumns
      ? [
          {
            Header: 'Weekday',
            id: 'weekday',
            Cell: ({ row }: CellProps<AthleteEvent>) => getDayFromDate(row.original.startDate),
            disableSortBy: true,
          },
          {
            Header: 'Bulk Added',
            accessor: 'isBulkAdd' as const,
            Cell: ({ value }: CellProps<AthleteEvent>) =>
              value ? (
                <IconCircleCheckFilled style={{ color: theme.colors.kiloBlue[6] }} />
              ) : (
                <IconCircleOff color='gray' />
              ),
            disableSortBy: true,
          },
        ]
      : []),
    {
      Header: 'Credited',
      accessor: 'isCredited',
      Cell: ({ value }: CellProps<AthleteEvent>) =>
        value ? (
          <IconCircleCheckFilled style={{ color: theme.colors.kiloBlue[6] }} />
        ) : (
          <IconCircleOff color='gray' />
        ),
      disableSortBy: true,
    },
    {
      id: 'action',
      Cell: ({ row }: CellProps<AthleteEvent>) => (
        <Group position='right' px='xs'>
          <Menu position='bottom-end' items={getMenuItems(row.original)} disabled={!permission} />
        </Group>
      ),
      minWidth: '50px',
      maxWidth: '50px',
      sticky: true,
    },
  ];

  return (
    <>
      <Group position='right' mx={margin} sx={{ height: 40, alignItems: 'center' }}>
        <Transition mounted={selectedIds.size > 0 && permission} transition='fade' duration={150}>
          {styles => (
            <Button
              style={styles}
              leftIcon={<IconTrash size={16} />}
              color='red'
              variant='light'
              size='sm'
              onClick={handleBulkDelete}
            >
              Delete {selectedIds.size} selected
            </Button>
          )}
        </Transition>
      </Group>
      <Table
        tableData={tableData}
        columns={columnsOverride || columns}
        isSuccess={isSuccess}
        isLoading={isLoading}
        isError={isError}
        isFetchingNextPage={isFetchingNextPage}
        hasNextPage={!!hasNextPage}
        fetchNextPage={fetchNextPage}
        clickableHeaders={
          showProfileParityColumns ? ['startDate', 'name', 'coachName'] : ['startDate', 'name']
        }
        handleHeaderClick={handleHeaderClick}
        ascending={ascending}
        ordering={ordering}
        bgc={bgc}
        margin={margin}
      />
      <DeleteAttendanceModal
        opened={deleteModalOpen}
        onClose={handleDeleteModalClose}
        selectedEvents={selectedEvents}
      />
      {selectedLcNsEvent && !!(lcNsClassData || lcNsAppointmentData) && (
        <ModalLateCancelNoShow
          opened
          isLateCancel={lcNsIsLateCancel}
          eventType={selectedLcNsEvent.type === 'athlete_kilo_class' ? 'class' : 'appointment'}
          athleteEventId={selectedLcNsEvent.athleteEventId}
          athleteId={searchFilters.athlete ?? ''}
          athleteName={athleteName}
          eventId={selectedLcNsEvent.eventId}
          eventName={lcNsEventName}
          gymId={lcNsClassData?.data.gym ?? lcNsAppointmentData?.data.gym}
          cancellationSettings={lcNsCancellationSettings}
          handleClose={() => setSelectedLcNsEvent(null)}
          triggeredBy='attendance'
        />
      )}
    </>
  );
};

export default AthleteAttendanceTableContainer;
